import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmtDateTimeCST } from '../../utils/datetime';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { companyApi, screeningApi } from '../../services/api';
import type { CompanyProfile } from '../../types';
import {
  BID_FIT_LABELS,
  DECISION_CLASSES,
  GATE_CLASSES,
  GATE_LABELS,
  LOST_TYPE_CLASSES,
  LOST_TYPE_LABELS,
  SCORING_MODE_LABELS,
  VERDICT_CLASSES,
  VERDICT_LABELS,
  VERDICT_OPTIONS,
  CATEGORY_LABELS,
  pickErrorMessage,
  type ScreeningCompanyComparison,
  type ScreeningCompanyResult,
  type ScreeningObjectiveLossItem,
  type ScreeningVerdict,
} from '../../types/screening';

interface CompanyComparisonProps {
  opportunityId: number;
  /** 公告要求条数，为 0 时不能对比（先用「解析公告要点」抽取要求清单） */
  requirementCount: number;
  /** 对比完成后通知父级刷新（对比结论可能回写投标决策） */
  onCompleted?: () => void;
}

/** 最多可选企业数（与后端 MAX_COMPARE_COMPANIES 保持一致） */
const MAX_COMPARE_COMPANIES = 6;

const formatScore = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

const attachmentTotal = (company: CompanyProfile): number =>
  Object.values(company.attachment_count || {}).reduce((sum, count) => sum + (count || 0), 0);

/**
 * 多企业资质对比与「是否适合投标」审核：
 * 选多家企业按同一份要求清单逐家审核客观分（资质/业绩/人员/财务/信用）预估失分，
 * 与价格分容忍额度（价格分 × 20%）比较后给出每家「是否适合投标」的结论，并推荐适合投标的企业。
 */
export default function CompanyComparison({
  opportunityId,
  requirementCount,
  onCompleted,
}: CompanyComparisonProps) {
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [comparison, setComparison] = useState<ScreeningCompanyComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** 刚完成一次审核：用于把结果滚入视野，避免"点完没看到结果"的错觉 */
  const [justCompleted, setJustCompleted] = useState(false);
  /** 当前选中的参评企业（一家公司一个 tab，逐条确认该企业的判定） */
  const [activeCompanyId, setActiveCompanyId] = useState<number | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (justCompleted && comparison) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setJustCompleted(false);
    }
  }, [justCompleted, comparison]);

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const res = await companyApi.list();
      setCompanies(res.data.companies || []);
    } catch {
      // 企业列表加载失败不影响已有对比结果的展示
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  const loadComparison = useCallback(async () => {
    setLoading(true);
    try {
      const res = await screeningApi.getCompanyComparison(opportunityId);
      setComparison(res.data.data ?? null);
    } catch (err) {
      setError(pickErrorMessage(err, '加载历史审核结果失败'));
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    void loadComparison();
  }, [loadComparison]);

  // 审核的证据链只取本人名下的企业资料（后端 build_company_context 亦按 user_id 校验归属），
  // 他人共享的企业不参与对比，避免选中后报「无权访问」
  const ownedCompanies = useMemo(
    () => companies.filter((company) => company.source !== 'shared'),
    [companies],
  );
  const sharedCount = companies.length - ownedCompanies.length;

  const toggleCompany = (companyId: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      }
      if (prev.length >= MAX_COMPARE_COMPANIES) {
        setError(`一次最多对比 ${MAX_COMPARE_COMPANIES} 家企业，请先取消部分选择`);
        return prev;
      }
      setError(null);
      return [...prev, companyId];
    });
  };

  const runCompare = async () => {
    if (selectedIds.length === 0) {
      setError('请先在上方选择参评企业；只选 1 家也能得到该企业是否适合投标的审核结论。');
      return;
    }
    setRunning(true);
    setError(null);
    setLogs(['正在准备各方企业资料…']);
    try {
      await screeningApi.compareCompaniesStream(opportunityId, selectedIds, (event) => {
        if (event.error) {
          throw new Error(event.message || '是否适合投标审核失败');
        }
        if (event.type === 'progress' && event.message) {
          setLogs((prev) => [...prev, event.message as string]);
        }
        if (event.type === 'result' && event.comparison) {
          const result = event.comparison;
          setComparison(result);
          setJustCompleted(true);
          setLogs((prev) => [
            ...prev,
            `审核完成：结论「${BID_FIT_LABELS[result.conclusion]}」` +
              (result.recommended_company_name
                ? `，推荐「${result.recommended_company_name}」投标`
                : '，没有适合投标的企业'),
          ]);
          onCompleted?.();
        }
      });
    } catch (err) {
      setError(pickErrorMessage(err, '是否适合投标审核失败'));
    } finally {
      setRunning(false);
    }
  };

  /** 按企业取数表：company_profile_id → seq → 该企业该条的审核明细 */
  const itemMap = useMemo(() => {
    const map: Record<number, Record<number, ScreeningCompanyResult['items'][number]>> = {};
    (comparison?.companies || []).forEach((company) => {
      map[company.company_profile_id] = {};
      company.items.forEach((item) => {
        map[company.company_profile_id][item.seq] = item;
      });
    });
    return map;
  }, [comparison]);

  const requirements = useMemo(() => comparison?.requirements || [], [comparison]);
  const matrixCompanies = useMemo(() => comparison?.companies || [], [comparison]);
  /** 选中企业：默认第 1 名 */
  const activeCompany =
    matrixCompanies.find((item) => item.company_profile_id === activeCompanyId) ??
    matrixCompanies[0] ??
    null;
  /** 审核结果变化时校正选中项（企业组合变了/结果被清空） */
  useEffect(() => {
    if (matrixCompanies.length === 0) {
      setActiveCompanyId(null);
      return;
    }
    if (!matrixCompanies.some((item) => item.company_profile_id === activeCompanyId)) {
      setActiveCompanyId(matrixCompanies[0].company_profile_id);
    }
  }, [matrixCompanies, activeCompanyId]);
  const scoringModeHint = comparison ? SCORING_MODE_LABELS[comparison.scoring_mode] : '';
  /** 价格分与容忍额度对所有企业一致（都取自公告），取第一名企业的审核口径展示 */
  const auditRef = matrixCompanies[0];
  const priceScore = auditRef?.effective_price_score ?? auditRef?.price_score ?? null;
  const toleranceScore = auditRef?.tolerance_score ?? null;
  const fitCount = matrixCompanies.filter((item) => item.conclusion === 'go').length;
  const cautiousCount = matrixCompanies.filter((item) => item.conclusion === 'cautious').length;
  /** 全部门槛都无法确认时，失分按最保守口径计（拿不到分），不是"没算" */
  const allGatesUnconfirmed =
    matrixCompanies.length > 0 && matrixCompanies.every((item) => item.gate_status === 'unconfirmed');
  /** 本次审核中采用「企业 × 条目」人工确认判定的单元格数 */
  const manualConfirmedCount = comparison?.manual_confirmed ?? 0;
  /** 正在保存人工确认的明细行 ID（避免重复提交） */
  const [savingCellId, setSavingCellId] = useState<number | null>(null);
  const [cellNotice, setCellNotice] = useState<string | null>(null);

  /** 人工确认某家企业某条要求的判定（企业级：只对该企业生效，重跑审核时保留并优先） */
  const handleCellVerdict = async (
    cell: ScreeningCompanyResult['items'][number],
    verdict: ScreeningVerdict | '',
  ) => {
    setSavingCellId(cell.id);
    setCellNotice(null);
    try {
      const res = await screeningApi.updateCompanyScore(cell.id, { final_verdict: verdict });
      setCellNotice(res.data.message);
      await loadComparison();
    } catch (err) {
      setError(pickErrorMessage(err, '保存人工确认判定失败'));
    } finally {
      setSavingCellId(null);
    }
  };

  /** 单元格里的判定下拉：人工确认「该企业该条」是否满足（企业级，只影响该企业） */
  const renderVerdictSelect = (cell: ScreeningCompanyResult['items'][number]) => {
    const effective = cell.effective_verdict || cell.verdict;
    return (
      <select
        value={effective}
        disabled={savingCellId === cell.id}
        onChange={(event) => {
          const value = event.target.value;
          void handleCellVerdict(
            cell,
            value === '__model__' ? '' : (value as ScreeningVerdict),
          );
        }}
        className={`rounded-full border px-1.5 py-0.5 text-[11px] outline-none disabled:opacity-50 ${VERDICT_CLASSES[effective]}`}
      >
        {VERDICT_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {VERDICT_LABELS[option]}
          </option>
        ))}
        {cell.final_verdict && (
          <option value="__model__">恢复模型判定（{VERDICT_LABELS[cell.verdict]}）</option>
        )}
      </select>
    );
  };

  /** 单元格里标注判定来源：该企业该条已人工确认 */
  const renderVerdictSource = (cell: ScreeningCompanyResult['items'][number]) =>
    cell.verdict_source === 'manual' ? (
      <span
        className="rounded-full border border-indigo-200 bg-indigo-50 px-1.5 text-[10px] text-indigo-700"
        title="该企业该条为人工确认判定，重跑审核时保留并优先，模型未改判"
      >
        人工
      </span>
    ) : null;

  /** 该企业已人工确认的条目数 */
  const manualCountOf = (company: ScreeningCompanyResult) =>
    company.items.filter((item) => item.verdict_source === 'manual').length;

  /** 选中企业的客观分失分明细（seq → 失分项），用于逐条列表展示 */
  const activeLossMap = useMemo(() => {
    const map: Record<number, ScreeningObjectiveLossItem> = {};
    (activeCompany?.objective_loss_items || []).forEach((loss) => {
      map[loss.seq] = loss;
    });
    return map;
  }, [activeCompany]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">资质对比与决策（是否适合投标）</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            选择 1~6 家企业，按公告要求逐家核算客观分（资质 / 业绩 / 人员 / 财务 / 信用）预估失分，
            再与价格分容忍额度比较，逐家给出「是否适合投标」的结论：只选 1 家回答这个标值不值得投，
            选多家再横向排名并回答由哪家企业去投。某家企业是否满足某条要求，在下方按企业逐条确认
            （一家公司一个 tab，人工确认优先，重跑审核时保留）。
          </p>
        </div>
        {comparison && (
          <span className="text-xs text-gray-400">
            {comparison.company_count} 家参评 · {comparison.requirement_count} 条要求
            {comparison.model_name ? ` · ${comparison.model_name}` : ''}
            {comparison.created_at ? ` · ${fmtDateTimeCST(comparison.created_at)}` : ''}
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {requirementCount === 0 && (
        <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          还没有要求清单，请先点击上方「解析公告要点」，评分表由公告要求生成。
        </div>
      )}

      {/* 参评企业选择 */}
      <div className="mt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-gray-500">
            参评企业（已选 {selectedIds.length} / {MAX_COMPARE_COMPANIES}：选 1 家只审核该企业是否适合投标，
            选 2 家以上横向对比并推荐适合投标的企业）
          </p>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
            >
              清空选择
            </button>
          )}
        </div>

        {companiesLoading && <p className="mt-2 text-xs text-gray-400">正在加载企业信息库…</p>}
        {!companiesLoading && ownedCompanies.length === 0 && (
          <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
            企业信息库中还没有可参评的企业。请先在「我的设置 — 企业信息库」录入企业及其资质附件、财务数据，
            资料越完整，审核结论越可靠（资料不足的条目视为拿不到分，并标为「可补」）。
          </p>
        )}
        {sharedCount > 0 && (
          <p className="mt-2 text-xs text-gray-400">
            另有 {sharedCount} 家企业为他人共享，审核证据链需归属本人，暂不参与对比。
          </p>
        )}
        {ownedCompanies.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {ownedCompanies.map((company) => {
              const selected = selectedIds.includes(company.id);
              const files = attachmentTotal(company);
              const financials = (company.financial_infos || []).length;
              return (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => toggleCompany(company.id)}
                  className={`rounded-lg border px-3 py-1.5 text-left text-xs transition ${
                    selected
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title={`资质附件 ${files} 个 · 财务数据 ${financials} 条`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium">{company.name}</span>
                    {company.is_default && <span className="text-indigo-500">默认</span>}
                  </span>
                  <span className="mt-0.5 block text-gray-400">
                    资质附件 {files} · 财务 {financials}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void runCompare()}
            disabled={running || requirementCount === 0}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
          >
            {running && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {running
              ? '审核中…'
              : comparison
                ? '重新审核是否适合投标'
                : '开始审核是否适合投标'}
          </button>
          <span className="text-xs text-gray-500">
            逐家企业核算客观分失分与门槛，耗时与所选企业数量成正比（每家一次模型调用）。
          </span>
        </div>
      </div>

      {logs.length > 0 && (
        <div className="mt-3 max-h-32 overflow-y-auto rounded-lg bg-gray-900/90 px-3 py-2 font-mono text-xs leading-relaxed text-gray-100">
          {logs.map((log, index) => (
            <p key={`${log}-${index}`}>{log}</p>
          ))}
        </div>
      )}

      {loading && !comparison && (
        <p className="mt-4 text-sm text-gray-500">正在读取历史审核结果…</p>
      )}

      {comparison && (
        <>
          {/* 结论与推荐 */}
          <div ref={resultRef} className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-200 pb-2 text-xs text-gray-600">
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
                审核完成
              </span>
              <span>
                {comparison.company_count} 家企业 × {comparison.requirement_count} 条要求
              </span>
              <span>
                适合投标 {fitCount} 家 · 谨慎 {cautiousCount} 家
              </span>
              {priceScore !== null && (
                <span>
                  价格分 {formatScore(priceScore)} 分 · 容忍客观分失分{' '}
                  {toleranceScore !== null ? `${formatScore(toleranceScore)} 分` : '—'}
                </span>
              )}
              <span>· {scoringModeHint}</span>
              {manualConfirmedCount > 0 && (
                <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-indigo-700">
                  已人工确认 {manualConfirmedCount} 条（企业级）
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-lg border px-3 py-1 text-sm font-medium ${DECISION_CLASSES[comparison.conclusion]}`}
              >
                {BID_FIT_LABELS[comparison.conclusion]}
              </span>
              {comparison.recommended_company_name ? (
                <span className="text-sm text-gray-800">
                  推荐 <span className="font-medium">{comparison.recommended_company_name}</span> 投标
                </span>
              ) : (
                <span className="text-sm text-rose-700">
                  没有适合投标的企业（须「硬性门槛明确通过且客观分失分在容忍额度内」）
                </span>
              )}
            </div>
            {auditRef?.loss_note && (
              <p className="mt-2 text-xs text-gray-500">判定依据：{auditRef.loss_note}</p>
            )}
            {allGatesUnconfirmed && (
              <p className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                注意：参评企业的硬性门槛几乎全部「无法确认」，这些条目按「拿不到分」计（不是没算），
                审核结论最多只能到「谨慎投标」。请在下方按企业逐条确认或补齐证据，
                或先把企业资料补进「企业信息库」，失分与结论会随之变化。
              </p>
            )}
            {comparison.scoring_mode === 'equal' && (
              <p className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                本次公告未解析出评分项分值，客观分失分按等权口径折算。建议对本项目重新执行一次「解析公告要点」，
                让评分项带上公告原分值后再审核，失分才等价于真实评标失分。
              </p>
            )}
            {comparison.recommendation && (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {comparison.recommendation}
              </p>
            )}
            {comparison.summary_md && (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {comparison.summary_md}
              </p>
            )}
            {comparison.risks?.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-800">
                {comparison.risks.map((item, index) => (
                  <li key={`cmp-risk-${index}`}>{item}</li>
                ))}
              </ul>
            )}
          </div>

          {/* 按企业确认：一家公司一个 tab */}
          <div className="mt-3">
            <div className="flex gap-1 overflow-x-auto pb-1">
              {matrixCompanies.map((company) => {
                const active = company.company_profile_id === activeCompany?.company_profile_id;
                const recommended = company.company_profile_id === comparison.recommended_company_id;
                const manualCount = manualCountOf(company);
                return (
                  <button
                    key={company.company_profile_id}
                    type="button"
                    onClick={() => setActiveCompanyId(company.company_profile_id)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition ${
                      active
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
                        company.rank === 1 ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {company.rank}
                    </span>
                    <span className="font-medium">{company.company_name}</span>
                    <span
                      className={`rounded-full border px-1.5 text-[11px] ${
                        DECISION_CLASSES[company.conclusion || 'no_go']
                      }`}
                    >
                      {BID_FIT_LABELS[company.conclusion || 'no_go']}
                    </span>
                    {recommended && (
                      <span className="rounded-full border border-indigo-200 bg-white px-1.5 text-[11px] text-indigo-700">
                        推荐
                      </span>
                    )}
                    {manualCount > 0 && (
                      <span className="text-[11px] text-indigo-600">人工 {manualCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {activeCompany && (
              <div className="mt-2 rounded-lg border border-gray-200">
                {/* 该企业结论摘要 */}
                <div className="space-y-1.5 border-b border-gray-100 bg-gray-50/70 px-3 py-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      className={`rounded-lg border px-2 py-0.5 text-xs font-medium ${
                        DECISION_CLASSES[activeCompany.conclusion || 'no_go']
                      }`}
                    >
                      {BID_FIT_LABELS[activeCompany.conclusion || 'no_go']}
                    </span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs ${GATE_CLASSES[activeCompany.gate_status]}`}
                    >
                      {GATE_LABELS[activeCompany.gate_status]}
                      {activeCompany.gate_status === 'unconfirmed' &&
                        ` ${activeCompany.mandatory_uncertain} 条`}
                    </span>
                    <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
                      第 {activeCompany.rank} 名
                    </span>
                    <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
                      计分项 {activeCompany.scoring_total} 条 / 满分{' '}
                      {formatScore(activeCompany.scoring_max)}
                    </span>
                    {manualCountOf(activeCompany) > 0 && (
                      <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                        人工确认 {manualCountOf(activeCompany)} 条
                      </span>
                    )}
                    {!activeCompany.qualified && (
                      <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                        不合格（硬性门槛不满足，一票否决）
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    得分参考 {formatScore(activeCompany.total_score)} / 100
                    {activeCompany.potential_score > activeCompany.total_score &&
                      `（资料补齐后可达 ${formatScore(activeCompany.potential_score)}）`}
                    {activeCompany.objective_lost !== null &&
                      activeCompany.objective_lost !== undefined && (
                        <>
                          {' · '}客观分预估失分{' '}
                          <span className="font-medium text-rose-600">
                            {formatScore(activeCompany.objective_lost)}
                          </span>
                          {activeCompany.tolerance_score !== null &&
                            activeCompany.tolerance_score !== undefined &&
                            ` / 容忍 ${formatScore(activeCompany.tolerance_score)} 分`}
                        </>
                      )}
                  </p>
                  {((activeCompany.evidence_missing_lost ?? 0) > 0 ||
                    (activeCompany.not_satisfied_lost ?? 0) > 0) && (
                    <p className="text-xs text-gray-600">
                      可补 {formatScore(activeCompany.evidence_missing_lost ?? 0)} 分 / 不可补{' '}
                      {formatScore(activeCompany.not_satisfied_lost ?? 0)} 分
                    </p>
                  )}
                  {(activeCompany.objective_loss_items || []).length > 0 && (
                    <div className="space-y-0.5 text-xs">
                      {(activeCompany.objective_loss_items || []).slice(0, 5).map((loss) => (
                        <p key={`loss-${loss.seq}`} className="leading-relaxed text-gray-600">
                          <span className="text-gray-400">#{loss.seq}</span>{' '}
                          {loss.requirement.slice(0, 26)}
                          <span className="ml-1 font-medium text-rose-600">
                            -{formatScore(loss.lost_score)} 分
                          </span>
                          <span
                            className={`ml-1 rounded-full border px-1.5 ${LOST_TYPE_CLASSES[loss.lost_type]}`}
                          >
                            {LOST_TYPE_LABELS[loss.lost_type]}
                          </span>
                        </p>
                      ))}
                      {(activeCompany.objective_loss_items || []).length > 5 && (
                        <p className="text-gray-400">
                          另有 {(activeCompany.objective_loss_items || []).length - 5} 条客观分失分项
                        </p>
                      )}
                    </div>
                  )}
                  {(activeCompany.strengths.length > 0 || activeCompany.weaknesses.length > 0) && (
                    <div className="space-y-0.5 text-xs">
                      {activeCompany.strengths.length > 0 && (
                        <p className="text-emerald-700">
                          优势：{activeCompany.strengths.join('；')}
                        </p>
                      )}
                      {activeCompany.weaknesses.length > 0 && (
                        <p className="text-amber-700">
                          短板：{activeCompany.weaknesses.join('；')}
                        </p>
                      )}
                    </div>
                  )}
                  {activeCompany.summary && (
                    <p className="text-xs leading-relaxed text-gray-600">
                      {activeCompany.summary}
                    </p>
                  )}
                </div>

                {/* 逐条要求与判定 */}
                <p className="border-b border-gray-100 px-3 py-2 text-xs text-gray-500">
                  逐条确认「{activeCompany.company_name}」是否满足：下拉选择即保存（企业级确认，
                  只影响该企业，重跑审核时保留并优先）；标「人工」为已确认，重新执行审核后结论与排名才会刷新
                </p>
                {cellNotice && (
                  <p className="border-b border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                    {cellNotice}
                  </p>
                )}
                <div className="divide-y divide-gray-100">
                  {requirements.length === 0 && (
                    <p className="px-3 py-6 text-center text-xs text-gray-500">暂无要求条目</p>
                  )}
                  {requirements.map((requirement) => {
                    const cell = itemMap[activeCompany.company_profile_id]?.[requirement.seq];
                    const loss = activeLossMap[requirement.seq];
                    return (
                      <div
                        key={requirement.seq}
                        className={`px-3 py-2.5 ${requirement.is_mandatory ? 'bg-rose-50/40' : ''}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-xs text-gray-400">#{requirement.seq}</span>
                              <span className="text-xs text-gray-500">
                                {CATEGORY_LABELS[requirement.category]}
                              </span>
                              {requirement.is_mandatory ? (
                                <span className="rounded-full border border-rose-200 bg-white px-1.5 text-[11px] text-rose-700">
                                  硬性门槛
                                </span>
                              ) : (
                                <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[11px] text-gray-600">
                                  {formatScore(requirement.max_score)} 分
                                </span>
                              )}
                              {cell && renderVerdictSource(cell)}
                            </div>
                            <p className="mt-1 text-sm leading-relaxed text-gray-800">
                              {requirement.requirement}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {cell ? (
                              <>
                                {!requirement.is_mandatory && (
                                  <span className="text-xs text-gray-700">
                                    {formatScore(cell.score)}
                                    <span className="text-gray-400">
                                      /{formatScore(cell.max_score)}
                                    </span>
                                  </span>
                                )}
                                {renderVerdictSelect(cell)}
                              </>
                            ) : (
                              <span className="text-xs text-gray-300">无该企业判定</span>
                            )}
                          </div>
                        </div>
                        {cell?.evidence && (
                          <p className="mt-1 text-xs text-gray-500">证据：{cell.evidence}</p>
                        )}
                        {loss && (
                          <p className="mt-1 text-xs text-gray-600">
                            失分{' '}
                            <span className="font-medium text-rose-600">
                              -{formatScore(loss.lost_score)}
                            </span>{' '}
                            分
                            <span
                              className={`ml-1 rounded-full border px-1.5 ${LOST_TYPE_CLASSES[loss.lost_type]}`}
                            >
                              {LOST_TYPE_LABELS[loss.lost_type]}
                            </span>
                          </p>
                        )}
                        {!loss &&
                          cell &&
                          !requirement.is_mandatory &&
                          cell.verdict === 'unknown' && (
                            <p className="mt-1 text-xs text-amber-700">
                              资料不足，按拿不到分计（可补）
                            </p>
                          )}
                      </div>
                    );
                  })}
                </div>

                {/* 合计 */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  <span className="font-medium">合计（得分参考 / 是否适合投标）</span>
                  <span>{formatScore(activeCompany.total_score)} / 100</span>
                  {activeCompany.objective_lost !== null &&
                    activeCompany.objective_lost !== undefined && (
                      <span className="text-rose-600">
                        客观分失分 {formatScore(activeCompany.objective_lost)} 分
                      </span>
                    )}
                  <span>{BID_FIT_LABELS[activeCompany.conclusion || 'no_go']}</span>
                </div>
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-gray-500">
            判定口径：判定「满足」按满分计，资料不足（待确认）按拿不到分计并标为「可补」；
            是否适合投标由「客观分预估失分 vs 价格分容忍额度（价格分 × 20%）」决定，
            排名顺序为「先看硬性门槛，再看是否适合投标、客观分失分多少」，评标得分仅作横向参考，
            硬性门槛不满足者即便分数最高也不参与推荐。逐条确认按「企业 × 条目」保存，
            只影响该企业，重跑审核时自动沿用。
          </p>
        </>
      )}
    </div>
  );
}
